import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import { Button, Select } from '@surecompanies/core_components'
import { Modal, notification } from 'antd'
import BulkUpdateTable from 'components/Table/BulkUpdate/BulkUpdateTable'
import { ColumnConfig } from 'components/Table/BulkUpdate/types'
import dayjs from 'dayjs'
import ContentSection from 'layouts/ContentSection/ContentSection'
import { useBenefit } from 'modules/benefits/hooks/useBenefit'
import { useCompany } from 'modules/client-service/hooks/useCompany'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Benefit } from 'types/benefit'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { Link } from 'react-router'
import { postActivityLog } from 'modules/activity-log-service/helpers'
import { ActivityLogAction } from 'modules/activity-log-service/constants'
import useRenewalApplicationsModalContent from './RenewalApplicationsModalContentHelper'

export default function RenewalApplicationsModalContent({
  onClose,
}: {
  onClose?: () => void
}) {
  /* --------------------------------- States --------------------------------- */
  const credentials = useSelector<any>(authSelectors.getCredentials) as any
  const [prevYearBenefits, setPrevYearBenefits] = useState<Benefit[]>([])
  const [currentYearBenefits, setCurrentYearBenefits] = useState<Benefit[]>([])
  const [selectedClient, setSelectedClient] = useState<string | null>(null)
  const { queryRenewalApplications, renewalBulkUpdateMutation } = useBenefit()
  const { queryCompanies } = useCompany()
  const { data: companies, isLoading: isLoadingCompanies } = queryCompanies()
  const {
    data: renewalApplications,
    isFetching: isFetchingApplications,
    refetch,
  } = queryRenewalApplications(selectedClient || '', {
    enabled: !!selectedClient,
  })
  const { BeforeAfterCompare } = useRenewalApplicationsModalContent()
  const { mutate, isPending: isSubmitting } = renewalBulkUpdateMutation({
    mutationOptions: {
      onSuccess: () => {
        refetch()
        onClose?.()
        notification.success({
          message: 'Success',
          description: `${prevYearBenefits.length} renewal applications have been updated successfully.`,
        })

        const company = companies?.data.find((c) => c.id === selectedClient)
        postActivityLog({
          action: ActivityLogAction.BULK_RENEWAL_APPLICATIONS_UPDATE,
          description: `updated ${
            prevYearBenefits.length
          } renewal applications for ${company?.name ?? 'the client'}.`,
          additionalData: {
            previousYearBenefits: prevYearBenefits,
            currentYearBenefits: currentYearBenefits,
            clientName: company?.name,
          },
          targetName: company?.name,
        })
      },
    },
  })

  const columns: ColumnConfig<Benefit>[] = [
    {
      field: 'employeeName',
      headerName: 'Employee Name',
      render: (value, data) => {
        if (!data?.['employeePublicKey']) return
        return (
          <Link
            target="_blank"
            to={`/employees/${data['employeePublicKey']}/coverage`}
            style={{ color: '#1890ff', textDecoration: 'underline' }}
          >
            {value}
          </Link>
        )
      },
    },
    {
      field: 'id',
      headerName: 'Benefit',
      width: 500,
      render: (value, _, index) => {
        const currentYearBenefitId = currentYearBenefits?.[index]?.id
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '45%' }}>
              Last Year:{' '}
              <Link
                target="_blank"
                to={`/application-manager/${value}`}
                style={{ color: '#1890ff', textDecoration: 'underline' }}
              >
                {value?.slice(0, 6)}...{value?.slice(-6)}
              </Link>
            </div>
            <div style={{ width: '45%' }}>
              Current Year:{' '}
              <Link
                target="_blank"
                to={`/application-manager/${currentYearBenefitId}`}
                style={{ color: '#1890ff', textDecoration: 'underline' }}
              >
                {currentYearBenefitId?.slice(0, 6)}...
                {currentYearBenefitId?.slice(-6)}
              </Link>
            </div>
          </div>
        )
      },
    },
    {
      field: 'id',
      headerName: 'Status',
      width: 300,
      render: (value, data, index) =>
        BeforeAfterCompare({
          before: currentYearBenefits?.[index]?.carrierApplicationStatus,
          after: 'Validated',
        }),
    },
    {
      field: 'id',
      headerName: 'Member ID',
      width: 300,
      render: (value, data, index) =>
        BeforeAfterCompare({
          before: currentYearBenefits?.[index]?.planPolicyNumber,
          after: prevYearBenefits?.[index]?.planPolicyNumber,
        }),
    },
    {
      field: 'id',
      headerName: 'Virtual Card ID',
      render: (value, data, index) =>
        BeforeAfterCompare({
          before: currentYearBenefits?.[index]?.virtualCardId,
          after: prevYearBenefits?.[index]?.virtualCardId,
        }),
    },
    {
      field: 'id',
      headerName: 'Virtual Card Key',
      render: (value, data, index) =>
        BeforeAfterCompare({
          before: currentYearBenefits?.[index]?.virtualCardKey,
          after: prevYearBenefits?.[index]?.virtualCardKey,
        }),
    },
    {
      field: 'id',
      headerName: 'VCN Status',
      render: (value, data, index) =>
        BeforeAfterCompare({
          before: currentYearBenefits?.[index]?.vcnstatus,
          after: prevYearBenefits?.[index]?.vcnstatus,
        }),
    },
    {
      field: 'id',
      headerName: 'Payment Type',
      render: (value, data, index) =>
        BeforeAfterCompare({
          before:
            currentYearBenefits?.[index]?.carrierApplicationBinderPaymentType,
          after: prevYearBenefits?.[index]?.carrierApplicationBinderPaymentType,
        }),
    },
    {
      field: 'id',
      headerName: 'Autopay Enabled',
      render: (value, data, index) =>
        BeforeAfterCompare({
          before:
            currentYearBenefits?.[index]
              ?.carrierApplicationBinderPaymentAutopayEnabled,
          after:
            prevYearBenefits?.[index]
              ?.carrierApplicationBinderPaymentAutopayEnabled,
        }),
    },
  ]

  const isLoading = isLoadingCompanies || isFetchingApplications || isSubmitting
  const isDisabled = !selectedClient || isLoading
  const submittedBy = `${credentials.firstname} ${credentials.lastname}`

  /* --------------------------------- Methods -------------------------------- */
  const handleSubmit = () => {
    Modal.confirm({
      title: 'Confirm Renewals',
      content: (
        <div>
          Please confirm whether you would like to <br /> submit{' '}
          <span style={{ fontWeight: 'bold', color: 'red' }}>
            {prevYearBenefits.length}
          </span>{' '}
          applications for the renewal process ?
        </div>
      ),
      okText: 'Confirm',
      onOk: () => {
        const payload = prevYearBenefits.map((benefit, index) => {
          return {
            id: currentYearBenefits[index].id,
            virtualCardKey: benefit.virtualCardKey,
            virtualCardId: benefit.virtualCardId,
            vcnstatus: benefit.vcnstatus,
            planPolicyNumber: benefit.planPolicyNumber,
            carrierApplicationValidatedDate: dayjs().toDate(),
            carrierApplicationBinderPaymentAutopayEnabled:
              benefit.carrierApplicationBinderPaymentAutopayEnabled,
            carrierApplicationValidatedBy: submittedBy,
            carrierApplicationBinderPaymentType:
              benefit.carrierApplicationBinderPaymentType,
          } as Benefit
        })

        mutate({ benefits: payload as Benefit[] })
      },
    })
  }

  /* --------------------------------- Watches -------------------------------- */
  useEffect(() => {
    if (renewalApplications) {
      setPrevYearBenefits(renewalApplications.prevYearBenefits)
      setCurrentYearBenefits(renewalApplications.currentYearBenefits)
    }
  }, [renewalApplications])

  /* ---------------------------------- Doms ---------------------------------- */
  return (
    <ContentSection title="Renewals">
      <Stack space={16}>
        <Stack direction="row" space={8}>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '5px',
              color: '#8c8c8c',
              fontSize: '14px',
            }}
          >
            <InfoCircleOutlined style={{ fontSize: '16px' }} />
            <span>
              Process the renewals for employees who had the same carrier, plan,
              family shape, and address as last year
            </span>
          </div>
        </Stack>
        <Stack
          direction="row"
          space={16}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Stack
            direction="row"
            space={8}
            style={{
              display: 'flex',
              gap: '10px',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" space={8} style={{ alignItems: 'center' }}>
              <Select
                showSearch
                loading={isLoading}
                disabled={isLoading}
                style={{ width: 200 }}
                placeholder="Select a client"
                onChange={(value) => setSelectedClient(value)}
                options={companies?.data.map((company) => ({
                  label: company.name,
                  value: company.id,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              <Button
                icon={<SyncOutlined />}
                onClick={() => refetch()}
                loading={isLoading}
                disabled={isLoading}
              />
            </Stack>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={isDisabled || !prevYearBenefits.length}
              loading={isLoading}
            >
              Submit
            </Button>
          </Stack>
        </Stack>

        <BulkUpdateTable<Benefit>
          data={prevYearBenefits}
          setData={setPrevYearBenefits}
          columns={columns}
          options={{ disableAddRow: true, disableEditable: true }}
          style={{ height: '400px', width: '100%' }}
        />
      </Stack>
    </ContentSection>
  )
}
